import React, { useState } from 'react';
import {
    Button,
    Card,
    Col,
    Form,
    Input,
    Row,
    Space,
    Tag,
    Timeline,
    Typography,
    Alert
} from 'antd';
import {
    SearchOutlined,
    BarcodeOutlined,
    CreditCardOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    ArrowRightOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import PageTitle from '../../../components/common/PageTitle';
import { imeiHistory } from '../../../utility/mockData';

const { Text, Title, Paragraph } = Typography;

const ImeiSearch = () => {
    const config = {
        title: 'IMEI Search & Lifecycle History',
        subtitle: 'Lookup the comprehensive inventory, transit, sales, and service logs for specific device IMEIs.',
    };
    // --- IMEI SEARCH STATES ---
    const [imeiQuery, setImeiQuery] = useState('');
    const [imeiSearchResult, setImeiSearchResult] = useState(null);
    const [imeiError, setImeiError] = useState('');
    const [hasSearchedImei, setHasSearchedImei] = useState(false);


    // --- IMEI SEARCH LOGIC ---
    const handleImeiSearch = () => {
        setImeiError('');
        setImeiSearchResult(null);
        setHasSearchedImei(true);

        const sanitized = imeiQuery.trim();
        if (!sanitized) {
            setImeiError('Please enter an IMEI number.');
            return;
        }

        // Standard IMEI is 15 digits
        if (!/^\d{15}$/.test(sanitized)) {
            setImeiError('Invalid IMEI format. IMEI must be exactly 15 digits.');
            return;
        }

        const match = imeiHistory[sanitized];
        if (match) {
            setImeiSearchResult(match);
        } else {
            setImeiError('IMEI record not found in system.');
        }
    };



        const getTimelineIcon = (status) => {
            switch (status) {
                case 'Received':
                    return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />;
                case 'Transferred':
                    return <ArrowRightOutlined style={{ color: '#1890ff', fontSize: '16px' }} />;
                case 'Sold & Invoiced':
                    return <CreditCardOutlined style={{ color: '#fa8c16', fontSize: '16px' }} />;
                case 'Activated':
                    return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} />;
                case 'Service Inward':
                    return <ClockCircleOutlined style={{ color: '#f5222d', fontSize: '16px' }} />;
                default:
                    return <InfoCircleOutlined style={{ fontSize: '16px' }} />;
            }
        };

        const getStatusTagColor = (status) => {
            const colors = {
                'Activated': 'success',
                'Transferred': 'blue',
                'Under Service': 'error',
                'Sold': 'warning'
            };
            return colors[status] || 'default';
        };

        return (
            <PageTitle title={config.title} subtitle={config.subtitle}>
                <Row gutter={[24, 24]}>
                    <Col xs={24} lg={10}>
                        <Card title={<Space><BarcodeOutlined /> IMEI Lookup</Space>} style={{ borderRadius: '16px' }}>
                            <Space direction="vertical" size={20} style={{ width: '100%' }}>
                                <Paragraph type="secondary">
                                    Enter the 15-digit Device International Mobile Equipment Identity (IMEI) number to retrieve the audit trail and activation records.
                                </Paragraph>

                                <div style={{ background: '#f5f6fa', padding: '12px 14px', borderRadius: '8px', border: '1px dashed #bfbfbf' }}>
                                    <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 4 }}>Demo IMEIs to test:</Text>
                                    <Space direction="vertical" size={2}>
                                        <Text code style={{ cursor: 'pointer' }} onClick={() => setImeiQuery('358240051111111')}>358240051111111 (Galaxy A54)</Text>
                                        <Text code style={{ cursor: 'pointer' }} onClick={() => setImeiQuery('358240052222222')}>358240052222222 (iPhone 14)</Text>
                                        <Text code style={{ cursor: 'pointer' }} onClick={() => setImeiQuery('358240053333333')}>358240053333333 (Redmi - Under Service)</Text>
                                    </Space>
                                </div>

                                <Form layout="vertical" onFinish={handleImeiSearch}>
                                    <Form.Item
                                        label="IMEI Number (15 digits)"
                                        validateStatus={imeiError ? 'error' : ''}
                                        help={imeiError}
                                    >
                                        <Input
                                            size="large"
                                            placeholder="e.g. 358240051111111"
                                            maxLength={15}
                                            value={imeiQuery}
                                            onChange={(e) => {
                                                const val = e.target.value.replace(/\D/g, ''); // numeric only
                                                setImeiQuery(val);
                                                setImeiError('');
                                            }}
                                            prefix={<BarcodeOutlined />}
                                        />
                                    </Form.Item>
                                    <Button
                                        type="primary"
                                        icon={<SearchOutlined />}
                                        htmlType="submit"
                                        size="large"
                                        block
                                        style={{ background: '#175AA0', borderRadius: '8px' }}
                                    >
                                        View Complete History
                                    </Button>
                                </Form>
                            </Space>
                        </Card>
                    </Col>

                    <Col xs={24} lg={14}>
                        {!hasSearchedImei ? (
                            <Card style={{ borderRadius: '16px', textAlign: 'center', padding: '60px 20px' }}>
                                <ClockCircleOutlined style={{ fontSize: '50px', color: '#bfbfbf', marginBottom: '16px' }} />
                                <Title level={4}>Lifecycle Audit Log</Title>
                                <Paragraph type="secondary" style={{ maxWidth: '400px', margin: '0 auto' }}>
                                    Search for a device IMEI on the left to track its logistics transit, sales location, activation status, and active support tickets.
                                </Paragraph>
                            </Card>
                        ) : imeiSearchResult ? (
                            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                {/* Device Details Info Box */}
                                <Card style={{ borderRadius: '16px', borderLeft: '5px solid #175AA0' }} bodyStyle={{ padding: 20 }}>
                                    <Row justify="space-between" align="top">
                                        <Col xs={24} sm={16}>
                                            <Title level={4} style={{ margin: 0, color: '#102a43' }}>
                                                {imeiSearchResult.brand} {imeiSearchResult.modelName}
                                            </Title>
                                            <Text type="secondary" style={{ display: 'block', marginTop: '2px' }}>
                                                SKU: <Text strong>{imeiSearchResult.sku}</Text> • IMEI: <Text code>{imeiSearchResult.imei}</Text>
                                            </Text>
                                            <div style={{ marginTop: 8 }}>
                                                <Tag color="blue">{imeiSearchResult.warrantyStatus}</Tag>
                                            </div>
                                        </Col>
                                        <Col xs={24} sm={8} style={{ textAlign: 'right' }}>
                                            <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>CURRENT STATE</Text>
                                            <Tag color={getStatusTagColor(imeiSearchResult.currentStatus)} style={{ fontSize: '13px', padding: '4px 8px', borderRadius: '4px', marginTop: 4 }}>
                                                {imeiSearchResult.currentStatus}
                                            </Tag>
                                            {imeiSearchResult.customerName && (
                                                <Text style={{ display: 'block', fontSize: '12px', marginTop: 4 }}>Owner: {imeiSearchResult.customerName}</Text>
                                            )}
                                        </Col>
                                    </Row>
                                </Card>

                                {/* Timeline Audit Trail */}
                                <Card title={<Space><ClockCircleOutlined /> Detailed Lifecycle Timeline</Space>} style={{ borderRadius: '16px' }}>
                                    <Timeline
                                        mode="left"
                                        style={{ marginTop: '12px' }}
                                        items={imeiSearchResult.history.map((log) => ({
                                            dot: getTimelineIcon(log.status),
                                            children: (
                                                <div style={{ marginBottom: 12 }}>
                                                    <Row justify="space-between">
                                                        <Col><Text strong style={{ fontSize: '14px' }}>{log.status}</Text></Col>
                                                        <Col><Text type="secondary" style={{ fontSize: '12px' }}>{log.date}</Text></Col>
                                                    </Row>
                                                    <Text type="secondary" style={{ display: 'block', fontSize: '13px', color: '#175AA0' }}>
                                                        Location: {log.branchName}
                                                    </Text>
                                                    <Text style={{ fontSize: '13px', color: '#595959', display: 'block', marginTop: 2 }}>
                                                        {log.notes}
                                                    </Text>
                                                </div>
                                            )
                                        }))}
                                    />
                                </Card>
                            </Space>
                        ) : (
                            <Card style={{ borderRadius: '16px' }}>
                                <Alert
                                    message="No Record Found"
                                    description={`The IMEI number "${imeiQuery}" could not be located in our records. Please verify the number and try again.`}
                                    type="warning"
                                    showIcon
                                />
                            </Card>
                        )}
                    </Col>
                </Row>
            </PageTitle>
        );
};

export default ImeiSearch;
